<template>
  <div>
    <h3 class="heading-3 mb-16">
      {{ i18n(translations.title) }}
    </h3>

    <RevCard>
      <div ref="errorMessageBlock" class="flex">
        <RevInfoBlock
          v-if="errorMessage"
          class="mx-20 mb-20"
          data-test="payment-error"
          dismissable
          :icon="IconHeartExploded"
          :title="errorMessage?.title || ''"
          variant="warning"
        >
          <div class="body-2 mt-8">
            <p>{{ errorMessage?.description }}</p>
          </div>
        </RevInfoBlock>
      </div>

      <ErrorBoundary :owners="['bot-squad-payin-front']">
        <PaymentForm
          ref="paymentForm"
          :base-price="price"
          :create-payment
          :payment-methods="paymentMethods ?? []"
          :resume-payment-id
          @setup-error="handleSetupError"
          @submit-error="handleSubmitError"
          @submit-start="handleSubmitStart"
          @submit-success="handleSubmitSuccess"
        />
        <CheckoutSubmitButton :form="paymentForm" />
      </ErrorBoundary>
    </RevCard>

    <GDPRNotice page="payment" />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { Price } from '@backmarket/http-api'
import {
  type CreatePaymentResponse,
  type PaymentMethod,
  PaymentMethodCode,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import {
  CheckoutSubmitButton,
  PaymentForm,
  type PaymentFormCreateFunctionOptions,
  type PaymentFormInstance,
  type PaymentReadableMessage,
  type SetupErrorEvent,
  type SubmitErrorEvent,
  type SubmitStartEvent,
  type SubmitSuccessData,
  usePaymentRedirection,
} from '@backmarket/nuxt-layer-payment/form'
import { useMarketPaymentMethodsFor } from '@backmarket/nuxt-layer-payment/methods'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCard } from '@ds/components/Card'
import { IconHeartExploded } from '@ds/icons/IconHeartExploded'

import GDPRNotice from '~/scopes/checkout/components/GDPRNotice/GDPRNotice.vue'
import usePaymentErrors from '~/scopes/checkout/composables/usePaymentErrors'
import { CHECKOUT } from '~/scopes/checkout/routes-names'

import translations from './Payment.translations'

const tracking = useTracking()

const props = defineProps<{
  price: Price
  requestId: string
}>()

const emit = defineEmits<{
  (e: 'update'): void
  (e: 'loading', message: string): void
  (e: 'loaded'): void
}>()

const i18n = useI18n()
const route = useRoute()
const proceedRedirection = usePaymentRedirection({
  paymentResultLocation: (paymentId) => ({
    type: 'internal',
    name: CHECKOUT.GIFT_CARDS.PAYMENT_RESULT,
    params: { paymentId },
  }),
})

const proceedExternalRedirection = usePaymentRedirection({
  paymentResultLocation: () => ({
    type: 'external',
    href: 'backmarket://gcpp/success',
  }),
})

const $httpFetch = useHttpFetch()

const { handlePaymentErrors } = usePaymentErrors()

const paymentForm = ref<PaymentFormInstance | null>(null)
const errorMessage = ref<{ title: string; description: string } | null>(null)

const errorMessageBlock = ref()

const supportedPaymentMethodCodes = [PaymentMethodCode.CARD]

const { data: paymentMethods } = await useMarketPaymentMethodsFor({
  bagPrice: props.price,
  customMethodFilter: (methods) =>
    methods.filter((method) =>
      supportedPaymentMethodCodes.includes(
        method.bmCode as typeof PaymentMethodCode.CARD,
      ),
    ),
})

const resumePaymentId = computed(() => route.query.paymentId as string)

onMounted(() => {
  // Disable back/forward cache: https://web.dev/articles/bfcache/
  // Without this the user that goes back to the payment page will see an infinite loader
  // TODO: Handle the case where the user goes back to the payment page
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload()
    }
  })
})

const createPayment = async (
  _options: PaymentFormCreateFunctionOptions,
): Promise<CreatePaymentResponse> => {
  const method = _options.method as PaymentMethod

  try {
    const response = await $httpFetch('/api/gift-cards/v1/payments', {
      method: 'POST',
      body: {
        requestId: props.requestId,
        channel: 'WEB',
        paymentMethodCode: method.bmCode,
        gateway: method.pspCode.toUpperCase(),
        fraudData: _options.fraudData,
      },
    })

    return {
      paymentId: response.reference,
      gatewayReference: response.gateway?.intentReference,
      gatewayTokenReference: response.paymentTokenReference || undefined,
      // Need to be replace when the API will work correcly
      nextAction: undefined,
    }
  } catch (error) {
    await handlePaymentErrors(error as HttpApiError)

    throw error
  }
}

const handleSubmitStart = ({
  customLoadingMessage,
  paymentMethod,
}: SubmitStartEvent) => {
  errorMessage.value = null
  emit('loading', customLoadingMessage || i18n(translations.loadingPayment))
  tracking.trackClick({
    value: paymentMethod?.bmCode || '',
    name: '4-Payment_cta-pay',
    zone: 'funnel',
  })
}

const showErrorMessage = async (
  message: PaymentReadableMessage | undefined,
) => {
  if (message) {
    errorMessage.value = message
    errorMessageBlock.value?.scrollIntoView({ behavior: 'smooth' })
  }
}

const handleSetupError = async ({ message }: SetupErrorEvent) => {
  await showErrorMessage(message)
}

const handleSubmitSuccess = ({ redirection }: SubmitSuccessData) => {
  if (route.name === CHECKOUT.GIFT_CARDS.WEBVIEW.PAYMENT) {
    proceedExternalRedirection(redirection)
  } else {
    proceedRedirection(redirection)
  }
}

const handleSubmitError = async ({
  redirection,
  message,
}: SubmitErrorEvent) => {
  if (redirection) {
    proceedRedirection(redirection)

    return
  }

  emit('loaded')
  await showErrorMessage(message)
}
</script>
